import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, Plus, Search, Loader2, Pencil, Trash2, 
  Upload, MapPin, Users, Calendar, IndianRupee, Building2,
  AlertCircle, CheckCircle, Clock, Pause, XCircle, Download, SlidersHorizontal
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: Clock },
  on_hold: { label: 'On Hold', color: 'bg-yellow-100 text-yellow-700', icon: Pause },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-600', icon: XCircle },
  filled: { label: 'Filled', color: 'bg-purple-100 text-purple-700', icon: CheckCircle }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700' }
};

export default function RequirementsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [requirements, setRequirements] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  // Phones only: the filter selects live behind a single "Filters" button.
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [total, setTotal] = useState(0);
  
  // Upload & Parse Modal
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [aiProvider, setAiProvider] = useState('anthropic');
  const [uploading, setUploading] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [jdStoragePath, setJdStoragePath] = useState('');
  const [jdFilename, setJdFilename] = useState('');
  const [rawParsed, setRawParsed] = useState(null);
  
  // Check URL params for auto-opening JD upload modal with pre-selected client
  useEffect(() => {
    const clientId = searchParams.get('client_id');
    const clientName = searchParams.get('client_name');
    const openJdUpload = searchParams.get('open_jd_upload');
    
    if (clientId && openJdUpload === 'true') {
      // Pre-select the client and open the upload modal
      setSelectedClient(clientId);
      setUploadModalOpen(true);
      
      // Clear the URL params so refreshing doesn't re-trigger
      setSearchParams({});
      
      if (clientName) {
        toast.info(`Adding requirement for ${decodeURIComponent(clientName)}`);
      }
    }
  }, [searchParams, setSearchParams]);
  
  // Create/Edit Form
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingReq, setEditingReq] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    position_title: '',
    headcount: 1,
    location: '',
    ctc_min: '',
    ctc_max: '',
    ctc_currency: 'INR',
    deadline: '',
    priority: 'medium',
    status: 'open',
    notes: '',
    jd_skills_must: [],
    jd_skills_nice: [],
    jd_summary: ''
  });
  
  // Delete
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reqToDelete, setReqToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadClients = useCallback(async () => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/clients?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients || []);
      }
    } catch (e) {
      console.error('Error loading clients:', e);
    }
  }, []);

  const loadRequirements = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ats_token');
      const params = new URLSearchParams({ limit: '200' });
      if (search) params.append('search', search);
      if (filterClient && filterClient !== 'all') params.append('client_id', filterClient);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);

      const res = await fetch(`${API_URL}/api/requirements?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setRequirements(data.requirements || []);
        setTotal(data.total || 0);
      } else {
        toast.error('Failed to load requirements');
      }
    } catch (e) {
      console.error('Error loading requirements:', e);
      toast.error('Error loading requirements');
    }
    setLoading(false);
  }, [search, filterClient, filterStatus]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    loadRequirements();
  }, [loadRequirements]);

  // Group requirements by client
  const groupedRequirements = requirements.reduce((acc, req) => {
    const clientName = req.client_name || 'Unknown Client';
    if (!acc[clientName]) acc[clientName] = [];
    acc[clientName].push(req);
    return acc;
  }, {});

  const resetUploadModal = () => {
    setSelectedClient('');
    setSelectedFile(null);
    setParsedData(null);
    setJdStoragePath('');
    setJdFilename('');
    setRawParsed(null);
  };

  const handleFileUpload = async () => {
    if (!selectedClient) {
      toast.error('Please select a company first');
      return;
    }
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('ats_token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('client_id', selectedClient);
      formData.append('ai_provider', aiProvider);

      const res = await fetch(`${API_URL}/api/requirements/upload-jd`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setParsedData(data.parsed_data);
        setJdStoragePath(data.jd_storage_path);
        setJdFilename(data.jd_filename);
        setRawParsed(data.raw_parsed);
        toast.success('JD parsed successfully! Review and save.');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to parse JD');
      }
    } catch (e) {
      console.error('Upload error:', e);
      toast.error('Error uploading file');
    }
    setUploading(false);
  };

  const handleSaveFromParsed = async () => {
    if (!parsedData?.position_title) {
      toast.error('Position title is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('ats_token');
      const formData = new FormData();
      formData.append('client_id', selectedClient);
      formData.append('position_title', parsedData.position_title || '');
      formData.append('headcount', parsedData.headcount || 1);
      formData.append('location', parsedData.location || '');
      formData.append('ctc_min', parsedData.ctc_min || '');
      formData.append('ctc_max', parsedData.ctc_max || '');
      formData.append('ctc_currency', parsedData.ctc_currency || 'INR');
      formData.append('deadline', parsedData.deadline || '');
      formData.append('priority', 'medium');
      formData.append('notes', '');
      formData.append('jd_skills_must', JSON.stringify(parsedData.jd_skills_must || []));
      formData.append('jd_skills_nice', JSON.stringify(parsedData.jd_skills_nice || []));
      formData.append('jd_summary', parsedData.jd_summary || '');
      formData.append('jd_storage_path', jdStoragePath);
      formData.append('jd_filename', jdFilename);
      formData.append('jd_parsed_json', JSON.stringify(rawParsed || {}));

      const res = await fetch(`${API_URL}/api/requirements/create-from-jd`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success('Requirement created successfully');
        setUploadModalOpen(false);
        resetUploadModal();
        loadRequirements();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create requirement');
      }
    } catch (e) {
      console.error('Save error:', e);
      toast.error('Error saving requirement');
    }
    setSaving(false);
  };

  const resetFormData = () => {
    setFormData({
      client_id: '',
      position_title: '',
      headcount: 1,
      location: '',
      ctc_min: '',
      ctc_max: '',
      ctc_currency: 'INR',
      deadline: '',
      priority: 'medium',
      status: 'open',
      notes: '',
      jd_skills_must: [],
      jd_skills_nice: [],
      jd_summary: ''
    });
    setEditingReq(null);
  };

  const openEditModal = (req) => {
    setEditingReq(req);
    setFormData({
      client_id: req.client_id || '',
      position_title: req.position_title || '',
      headcount: req.headcount || 1,
      location: req.location || '',
      ctc_min: req.ctc_min || '',
      ctc_max: req.ctc_max || '',
      ctc_currency: req.ctc_currency || 'INR',
      deadline: req.deadline || '',
      priority: req.priority || 'medium',
      status: req.status || 'open',
      notes: req.notes || '',
      jd_skills_must: req.jd_skills_must || [],
      jd_skills_nice: req.jd_skills_nice || [],
      jd_summary: req.jd_summary || ''
    });
    setFormModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.position_title.trim()) {
      toast.error('Position title is required');
      return;
    }
    if (!formData.client_id) {
      toast.error('Please select a company');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('ats_token');
      const url = editingReq
        ? `${API_URL}/api/requirements/${editingReq.id}`
        : `${API_URL}/api/requirements`;

      const payload = {
        ...formData,
        ctc_min: formData.ctc_min ? parseFloat(formData.ctc_min) : null,
        ctc_max: formData.ctc_max ? parseFloat(formData.ctc_max) : null,
        headcount: parseInt(formData.headcount) || 1
      };

      const res = await fetch(url, {
        method: editingReq ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(editingReq ? 'Requirement updated' : 'Requirement created');
        setFormModalOpen(false);
        resetFormData();
        loadRequirements();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (e) {
      console.error('Save error:', e);
      toast.error('Error saving requirement');
    }
    setSaving(false);
  };

  const confirmDelete = (req) => {
    setReqToDelete(req);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!reqToDelete) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/requirements/${reqToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Requirement deleted');
        setDeleteConfirmOpen(false);
        setReqToDelete(null);
        loadRequirements();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete');
      }
    } catch (e) {
      console.error('Delete error:', e);
      toast.error('Error deleting requirement');
    }
    setDeleting(false);
  };

  // ctc_unit === 'absolute' means the figures are whole rupees (JM legacy data).
  // Anything else (or absent) keeps the ATS default of lakhs per annum.
  const formatCTC = (min, max, currency, unit) => {
    if (!min && !max) return '-';
    const curr = currency === 'INR' ? '₹' : (currency || '₹');
    const absolute = unit === 'absolute';
    const n = (v) => absolute ? Number(v).toLocaleString('en-IN') : v;
    const suffix = absolute ? ' p.a.' : ' LPA';
    if (min && max) return `${curr}${n(min)} - ${curr}${n(max)}${suffix}`;
    if (min) return `${curr}${n(min)}+${suffix}`;
    if (max) return `Up to ${curr}${n(max)}${suffix}`;
    return '-';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6 max-md:flex-col max-md:items-stretch max-md:gap-3">
          <div className="max-md:min-w-0">
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 max-md:text-xl">
              <FileText className="w-7 h-7 text-slate-600 max-md:shrink-0" />
              Requirements
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage job descriptions and hiring requirements
            </p>
          </div>
          <div className="flex items-center gap-2 max-md:grid max-md:grid-cols-2 [&>button]:max-md:min-h-[44px] [&>button]:max-md:w-full">
            <Button variant="outline" onClick={() => { resetFormData(); setFormModalOpen(true); }} data-testid="add-manual-req-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Manual
            </Button>
            <Button onClick={() => { resetUploadModal(); setUploadModalOpen(true); }} data-testid="upload-jd-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload JD
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-3">
              <button
                type="button"
                onClick={() => setMobileFiltersOpen((v) => !v)}
                className="md:hidden order-2 inline-flex items-center justify-center gap-2 min-h-[44px] w-full rounded-lg border border-slate-200 bg-white text-sm text-slate-700"
                aria-expanded={mobileFiltersOpen}
                data-testid="req-mobile-filters-toggle"
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filters
              </button>
              <div className="relative flex-1 min-w-[200px] max-w-md max-md:order-1 max-md:max-w-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by title, location..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="req-search-input"
                />
              </div>
              <Select value={filterClient} onValueChange={setFilterClient}>
                <SelectTrigger className={`w-[180px] max-md:order-3 max-md:w-full max-md:h-[44px] ${mobileFiltersOpen ? '' : 'max-md:hidden'}`} data-testid="filter-client">
                  <SelectValue placeholder="All Companies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className={`w-[140px] max-md:order-4 max-md:w-full max-md:h-[44px] ${mobileFiltersOpen ? '' : 'max-md:hidden'}`} data-testid="filter-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Badge variant="secondary" className="text-sm max-md:order-5 max-md:self-start">
                {total} {total === 1 ? 'Requirement' : 'Requirements'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Requirements Table Grouped by Client */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : requirements.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="font-medium">No requirements found</p>
              <p className="text-sm mt-1">Upload a JD or add a requirement manually</p>
              <Button onClick={() => setUploadModalOpen(true)} className="mt-4">
                <Upload className="w-4 h-4 mr-2" />
                Upload JD
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedRequirements).map(([clientName, reqs]) => (
              <Card key={clientName}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="w-5 h-5 text-slate-500" />
                    {clientName}
                    <Badge variant="outline" className="ml-2">{reqs.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto max-md:hidden">
                  <table className="w-full max-md:hidden">
                    <thead className="bg-slate-50 border-y">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Position</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">CTC</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-slate-500 uppercase">Headcount</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Deadline</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Priority</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {reqs.map(req => {
                        const statusCfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.open;
                        const priorityCfg = PRIORITY_CONFIG[req.priority] || PRIORITY_CONFIG.medium;
                        const StatusIcon = statusCfg.icon;
                        
                        return (
                          <tr key={req.id} className="hover:bg-slate-50" data-testid={`req-row-${req.id}`}>
                            <td className="px-4 py-3">
                              <Link 
                                to={`/ats/requirements/${req.id}`} 
                                className="font-medium text-slate-800 hover:text-blue-600 hover:underline"
                              >
                                {req.position_title}
                              </Link>
                              {req.jd_summary && (
                                <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{req.jd_summary}</div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {req.location ? (
                                <div className="flex items-center gap-1 text-sm text-slate-600">
                                  <MapPin className="w-3 h-3" />
                                  {req.location}
                                </div>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1 text-sm text-slate-600">
                                <IndianRupee className="w-3 h-3" />
                                {formatCTC(req.ctc_min, req.ctc_max, req.ctc_currency, req.ctc_unit)}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1 text-sm">
                                <Users className="w-3 h-3 text-slate-400" />
                                {req.headcount}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {req.deadline ? (
                                <div className="flex items-center gap-1 text-sm text-slate-600">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(req.deadline)}
                                </div>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={`${statusCfg.color} flex items-center gap-1 w-fit`}>
                                <StatusIcon className="w-3 h-3" />
                                {statusCfg.label}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={priorityCfg.color}>{priorityCfg.label}</Badge>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {req.jd_storage_path && (
                                  <a
                                    href={`${API_URL}/api/requirements/${req.id}/download-jd`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="p-1.5 text-slate-400 hover:text-green-600 rounded"
                                    title="Download JD"
                                  >
                                    <Download className="w-4 h-4" />
                                  </a>
                                )}
                                <button
                                  onClick={() => openEditModal(req)}
                                  className="p-1.5 text-slate-400 hover:text-blue-600 rounded"
                                  title="Edit"
                                  data-testid={`edit-req-${req.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => confirmDelete(req)}
                                  className="p-1.5 text-slate-400 hover:text-red-600 rounded"
                                  title="Delete"
                                  data-testid={`delete-req-${req.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>

                  {/* Phones: the same requirements as stacked cards. */}
                  <div className="md:hidden divide-y divide-slate-100 border-t">
                    {reqs.map(req => {
                      const statusCfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.open;
                      const priorityCfg = PRIORITY_CONFIG[req.priority] || PRIORITY_CONFIG.medium;
                      const StatusIcon = statusCfg.icon;

                      return (
                        <div key={req.id} className="p-4 space-y-3" data-testid={`req-card-${req.id}`}>
                          <div>
                            <Link
                              to={`/ats/requirements/${req.id}`}
                              className="font-semibold text-base text-slate-800 hover:text-blue-600 break-words"
                            >
                              {req.position_title}
                            </Link>
                            {req.jd_summary && (
                              <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{req.jd_summary}</div>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <Badge className={`${statusCfg.color} flex items-center gap-1 w-fit`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusCfg.label}
                            </Badge>
                            <Badge className={priorityCfg.color}>{priorityCfg.label}</Badge>
                          </div>

                          <dl className="space-y-1.5 text-sm">
                            <div className="flex gap-2">
                              <dt className="w-24 shrink-0 text-xs uppercase tracking-wide text-slate-400 pt-0.5">Location</dt>
                              <dd className="min-w-0 flex-1 text-slate-600 truncate">{req.location || '-'}</dd>
                            </div>
                            <div className="flex gap-2">
                              <dt className="w-24 shrink-0 text-xs uppercase tracking-wide text-slate-400 pt-0.5">CTC</dt>
                              <dd className="min-w-0 flex-1 text-slate-600 truncate">
                                {formatCTC(req.ctc_min, req.ctc_max, req.ctc_currency, req.ctc_unit)}
                              </dd>
                            </div>
                            <div className="flex gap-2">
                              <dt className="w-24 shrink-0 text-xs uppercase tracking-wide text-slate-400 pt-0.5">Headcount</dt>
                              <dd className="min-w-0 flex-1 text-slate-600">{req.headcount}</dd>
                            </div>
                            <div className="flex gap-2">
                              <dt className="w-24 shrink-0 text-xs uppercase tracking-wide text-slate-400 pt-0.5">Deadline</dt>
                              <dd className="min-w-0 flex-1 text-slate-600 truncate">
                                {req.deadline ? formatDate(req.deadline) : '-'}
                              </dd>
                            </div>
                          </dl>

                          <div className="grid grid-cols-2 gap-2">
                            {req.jd_storage_path && (
                              <a
                                href={`${API_URL}/api/requirements/${req.id}/download-jd`}
                                target="_blank"
                                rel="noreferrer"
                                className="min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 text-xs text-slate-600"
                              >
                                <Download className="w-4 h-4" /> JD
                              </a>
                            )}
                            <button
                              onClick={() => openEditModal(req)}
                              className="min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 text-xs text-slate-600"
                              data-testid={`edit-req-card-${req.id}`}
                            >
                              <Pencil className="w-4 h-4" /> Edit
                            </button>
                            <button
                              onClick={() => confirmDelete(req)}
                              className="min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-lg border border-red-200 text-xs text-red-600"
                              data-testid={`delete-req-card-${req.id}`}
                            >
                              <Trash2 className="w-4 h-4" /> Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Upload JD Modal */}
        <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Upload Job Description
              </DialogTitle>
              <DialogDescription>
                Upload a JD file to automatically extract requirement details
              </DialogDescription>
            </DialogHeader>

            {!parsedData ? (
              <div className="space-y-4">
                <div>
                  <Label>Company *</Label>
                  <Select value={selectedClient} onValueChange={setSelectedClient}>
                    <SelectTrigger data-testid="upload-client-select">
                      <SelectValue placeholder="Select a company" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>JD File (PDF, DOCX, TXT) *</Label>
                  <Input
                    type="file"
                    accept=".pdf,.docx,.doc,.txt"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="mt-1"
                    data-testid="jd-file-input"
                  />
                </div>

                <div>
                  <Label>AI Provider</Label>
                  <Select value={aiProvider} onValueChange={setAiProvider}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="anthropic">Claude (Anthropic)</SelectItem>
                      <SelectItem value="openai">GPT-4 (OpenAI)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Configure API keys in Settings</p>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setUploadModalOpen(false)}>Cancel</Button>
                  <Button onClick={handleFileUpload} disabled={uploading || !selectedClient || !selectedFile} data-testid="parse-jd-btn">
                    {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Parse JD
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-800">JD Parsed Successfully</p>
                    <p className="text-sm text-green-700">Review and edit the extracted details below</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Position Title *</Label>
                    <Input
                      value={parsedData.position_title || ''}
                      onChange={(e) => setParsedData({...parsedData, position_title: e.target.value})}
                      data-testid="parsed-title-input"
                    />
                  </div>
                  <div>
                    <Label>Headcount</Label>
                    <Input
                      type="number"
                      min="1"
                      value={parsedData.headcount || 1}
                      onChange={(e) => setParsedData({...parsedData, headcount: parseInt(e.target.value) || 1})}
                    />
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input
                      value={parsedData.location || ''}
                      onChange={(e) => setParsedData({...parsedData, location: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>CTC Min (LPA)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={parsedData.ctc_min || ''}
                      onChange={(e) => setParsedData({...parsedData, ctc_min: parseFloat(e.target.value) || null})}
                    />
                  </div>
                  <div>
                    <Label>CTC Max (LPA)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={parsedData.ctc_max || ''}
                      onChange={(e) => setParsedData({...parsedData, ctc_max: parseFloat(e.target.value) || null})}
                    />
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <Select 
                      value={parsedData.ctc_currency || 'INR'} 
                      onValueChange={(v) => setParsedData({...parsedData, ctc_currency: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INR">INR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Deadline</Label>
                    <Input
                      type="date"
                      value={parsedData.deadline || ''}
                      onChange={(e) => setParsedData({...parsedData, deadline: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <Label>Must-Have Skills</Label>
                  <div className="flex flex-wrap gap-1 mt-1 p-2 border rounded-md min-h-[40px]">
                    {(parsedData.jd_skills_must || []).map((skill, idx) => (
                      <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                        {skill}
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...parsedData.jd_skills_must];
                            updated.splice(idx, 1);
                            setParsedData({...parsedData, jd_skills_must: updated});
                          }}
                          className="ml-1 hover:text-red-500"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Nice-to-Have Skills</Label>
                  <div className="flex flex-wrap gap-1 mt-1 p-2 border rounded-md min-h-[40px]">
                    {(parsedData.jd_skills_nice || []).map((skill, idx) => (
                      <Badge key={idx} variant="outline" className="flex items-center gap-1">
                        {skill}
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...parsedData.jd_skills_nice];
                            updated.splice(idx, 1);
                            setParsedData({...parsedData, jd_skills_nice: updated});
                          }}
                          className="ml-1 hover:text-red-500"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Summary</Label>
                  <Textarea
                    value={parsedData.jd_summary || ''}
                    onChange={(e) => setParsedData({...parsedData, jd_summary: e.target.value})}
                    rows={2}
                  />
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => { setParsedData(null); }}>Back</Button>
                  <Button onClick={handleSaveFromParsed} disabled={saving} data-testid="save-parsed-req-btn">
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Create Requirement
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Manual Add/Edit Modal */}
        <Dialog open={formModalOpen} onOpenChange={setFormModalOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingReq ? 'Edit Requirement' : 'Add Requirement'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <Label>Company *</Label>
                <Select value={formData.client_id} onValueChange={(v) => setFormData({...formData, client_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Position Title *</Label>
                <Input
                  value={formData.position_title}
                  onChange={(e) => setFormData({...formData, position_title: e.target.value})}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Headcount</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.headcount}
                    onChange={(e) => setFormData({...formData, headcount: parseInt(e.target.value) || 1})}
                  />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>CTC Min</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.ctc_min}
                    onChange={(e) => setFormData({...formData, ctc_min: e.target.value})}
                  />
                </div>
                <div>
                  <Label>CTC Max</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.ctc_max}
                    onChange={(e) => setFormData({...formData, ctc_max: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Currency</Label>
                  <Select value={formData.ctc_currency} onValueChange={(v) => setFormData({...formData, ctc_currency: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">INR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Deadline</Label>
                  <Input
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORITY_CONFIG).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {editingReq && (
                <div>
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={2}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setFormModalOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingReq ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Requirement</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{reqToDelete?.position_title}</strong>? This will also remove the attached JD file.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
                {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
